<script setup>
import { ref, reactive, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Button from '../../../../components-next/button/Button.vue';
import FormSection from '../../../../components/FormSection.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SortButton from 'dashboard/components/table/SortButton.vue';
import TruncatedCell from 'dashboard/components/table/tableMod/TruncatedCell.vue';
import BaseCell from 'dashboard/components/table/tableMod/BaseCellMod.vue';
import wootAPI from '@/v3/api/apiClient';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import axios from 'axios';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

// State
const isLoading = ref(true);
const agentData = reactive({
  id: null,
  name: '',
  voice: 'female',
  createdAt: null,
  updatedAt: null,
  company: {
    name: '',
    website: '',
    description: '',
    role: '',
    cnpj: '',
    locations: [],
    messageGrouping: 25,
    // schedulingSettings removido - gerenciado no profissional
    additionalInfo: [],
    support: {
      phone: '',
      email: '',
      faqLink: ''
    },
    policies: [],
    tagFields: [],
    teamFields: [],
    humanRedirectConditions: '',
    humanAvailabilityMessagingEnabled: false,
    humanAvailabilityHours: null,
    shoppingCartEnabled: false
  },
  userFields: [],
  personalityId: null,
  conversationFlowId: null,
  selectedInboxes: []
});

// State para controle do dropdown de inboxes
const showInboxDropdown = ref(false);
const inboxOptions = ref([]);
const loadingInboxes = ref(false);
const dropdownPosition = ref({ top: 0 });

// Props
const props = defineProps({
  isDuplicated: {
    type: Boolean,
    default: false
  },
  hasLinkedSchedule: {
    type: Boolean,
    default: false
  },
  scheduleStatus: {
    type: Object,
    default: () => ({ status: 'not_applicable', hasSchedule: false })
  }
});

// Controle de abas
const activeTab = ref('company');

// Days of week for display - mesma ordem do profissional
const daysOfWeekDisplay = [
  { key: 'sunday', label: 'Domingo' },
  { key: 'monday', label: 'Segunda-feira' },
  { key: 'tuesday', label: 'Terça-feira' },
  { key: 'wednesday', label: 'Quarta-feira' },
  { key: 'thursday', label: 'Quinta-feira' },
  { key: 'friday', label: 'Sexta-feira' },
  { key: 'saturday', label: 'Sábado' }
];

// Estado para armazenar se o Google Calendar está conectado
const isGoogleCalendarConnected = ref(false);

// Estado para controlar items expandidos
const expandedItems = ref({
  additionalInfo: [],
  policies: []
});

// Debug console state
const showDebugConsole = ref(false);
const isDebugMode = ref(false);
const debugAgentJson = ref('');
const showPasteJsonSection = ref(false);
const pastedJson = ref('');
const pasteJsonError = ref('');

// Computed para obter o accountId da URL
const accountId = computed(() => route.params.accountId);

// Computed para obter o agentId da URL
const agentId = computed(() => route.params.agentId);

// Computed para abas dinâmicas baseado no flow
const tabs = computed(() => {
  const baseTabs = [
    { id: 'company', label: 'Empresa', icon: 'i-lucide-building-2' },
    { id: 'locations', label: 'Localizações', icon: 'i-lucide-map-pin' },
    { id: 'fields', label: 'Atributos do Usuário', icon: 'i-lucide-users' },
    { id: 'behavior', label: 'Comportamento', icon: 'i-lucide-brain' },
    { id: 'technical', label: 'Configurações Técnicas', icon: 'i-lucide-settings' },
    { id: 'content', label: 'Base de Conhecimento', icon: 'i-lucide-file-text' }
  ];

  // Aba de horários removida - agendamento agora é gerenciado no profissional

  return baseTabs;
});

// Mapeamento para nomes legíveis
const voiceTypes = {
  'male': 'Voz Masculina',
  'female': 'Voz Feminina',
  'neutral': 'Neutra'
};

const personalityTypes = {
  1: 'Profissional',
  2: 'Amigável',
  3: 'Consultivo'
};

const flowTypes = {
  1: 'Qualificação',
  2: 'Vendas',
  3: 'Agendamento'
};

// Função para obter cookie
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

// Debug console methods
const checkDebugMode = async () => {
  try {
    // Check if current user has debug permissions
    const headers = getAuthHeaders();
    const response = await axios.get('/api/v1/check_debug_mode', { headers, withCredentials: true });
    const hasDebugPermission = response.data.debug_mode === true;

    // Enable debug based on user permissions
    isDebugMode.value = hasDebugPermission;
  } catch (error) {
    // If API fails, debug mode stays disabled
    isDebugMode.value = false;
  }
};

const toggleDebugConsole = () => {
  if (isDebugMode.value) {
    showDebugConsole.value = !showDebugConsole.value;
    if (showDebugConsole.value && agentData.id) {
      // Format agent data as JSON
      const agentJsonData = {
        id: agentData.id,
        name: agentData.name,
        voice: agentData.voice,
        personality: agentData.personalityId,
        flow: agentData.conversationFlowId,
        company_data: agentData.company,
        user_fields: agentData.userFields,
        selected_inboxes: agentData.selectedInboxes,
        created_at: agentData.createdAt,
        updated_at: agentData.updatedAt
      };
      debugAgentJson.value = JSON.stringify(agentJsonData, null, 2);
    } else {
      // Resetar estados ao fechar o modal
      showPasteJsonSection.value = false;
      pastedJson.value = '';
      pasteJsonError.value = '';
    }
  }
};

const copyJsonToClipboard = () => {
  if (debugAgentJson.value) {
    navigator.clipboard.writeText(debugAgentJson.value).then(() => {
      useAlert('JSON copiado para a área de transferência!');
    }).catch(() => {
      useAlert('Erro ao copiar JSON', 'error');
    });
  }
};

const togglePasteJsonSection = () => {
  showPasteJsonSection.value = !showPasteJsonSection.value;
  if (!showPasteJsonSection.value) {
    pastedJson.value = '';
    pasteJsonError.value = '';
  }
};

const validateAndUpdateAgent = async () => {
  try {
    pasteJsonError.value = '';

    // Validar se o JSON é válido
    if (!pastedJson.value.trim()) {
      pasteJsonError.value = 'Por favor, cole um JSON válido';
      return;
    }

    let parsedJson;
    try {
      parsedJson = JSON.parse(pastedJson.value);
    } catch (e) {
      pasteJsonError.value = 'JSON inválido. Verifique a sintaxe.';
      return;
    }

    // Validar campos obrigatórios
    if (!parsedJson.id) {
      pasteJsonError.value = 'O JSON deve conter um ID válido';
      return;
    }

    // IMPORTANTE: Validar que o ID do JSON colado corresponde ao agente atual
    if (parsedJson.id !== agentData.id) {
      pasteJsonError.value = `Erro: O JSON colado é do agente ID ${parsedJson.id}, mas você está visualizando o agente ID ${agentData.id}. Só é possível atualizar o agente atual.`;
      return;
    }

    // Confirmar antes de atualizar
    if (!confirm('Tem certeza que deseja atualizar este agente com os dados colados? Esta ação não pode ser desfeita.')) {
      return;
    }

    // Preparar dados para atualização no formato esperado pelo backend
    const updateData = {
      agent: {
        name: parsedJson.name,
        voice: parsedJson.voice,
        personalityId: parsedJson.personality,
        conversationFlowId: parsedJson.flow,
        company: parsedJson.company_data,
        userFields: parsedJson.user_fields
      },
      selected_inboxes: parsedJson.selected_inboxes || []
    };

    // Fazer requisição de atualização usando o ID do agente atual (não o do JSON)
    const headers = getAuthHeaders();
    const response = await axios.put(
      `/api/v1/agents_ai/${agentData.id}`,
      updateData,
      { headers, withCredentials: true }
    );

    if (response.status === 200) {
      useAlert('Agente atualizado com sucesso!');
      // Recarregar dados do agente
      await fetchAgent();
      // Fechar seção de colar
      showPasteJsonSection.value = false;
      pastedJson.value = '';
    }
  } catch (error) {
    console.error('Erro ao atualizar agente:', error);
    pasteJsonError.value = error.response?.data?.error || 'Erro ao atualizar agente. Verifique os dados e tente novamente.';
    useAlert(pasteJsonError.value);
  }
};

// Função para obter headers de autenticação
const getAuthHeaders = () => {
  const cookie = getCookie('cw_d_session_info');
  if (!cookie) {
    console.error('cw_d_session_info cookie não encontrado');
    return {};
  }
  try {
    const sessionInfo = JSON.parse(decodeURIComponent(cookie));
    return {
      'access-token': sessionInfo['access-token'],
      client: sessionInfo.client,
      uid: sessionInfo.uid,
    };
  } catch (e) {
    console.error('Erro ao analisar cw_d_session_info');
    return {};
  }
};

// Função para verificar se o Google Calendar está conectado
const checkGoogleCalendarConnection = async () => {
  try {
    const authHeaders = getAuthHeaders();
    
    const params = new URLSearchParams({
      start: new Date().toISOString(),
      end: new Date().toISOString()
    });
    
    const response = await fetch(`/api/v1/google_calendar?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders
      },
      credentials: 'include'
    });
    
    if (response.ok) {
      const data = await response.json();
      isGoogleCalendarConnected.value = true;
    } else {
      isGoogleCalendarConnected.value = false;
    }
  } catch (error) {
    isGoogleCalendarConnected.value = false;
  }
};

// Computed para verificar se tem agenda vinculada
const hasSchedule = computed(() => props.hasLinkedSchedule);

// Computed para obter o status da agenda
const scheduleStatus = computed(() => ({
  isCalendarConnected: isGoogleCalendarConnected.value,
  hasSchedule: hasSchedule.value
}));

// Função para formatar texto longo com ellipsis
const truncateText = (text, maxLength = 100) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Função para formatar endereço completo
const formatAddress = (location) => {
  if (!location) return '';
  return {
    street: `${location.street}, ${location.number}${location.complement ? ' - ' + location.complement : ''}`,
    neighborhood: location.neighborhood,
    city: `${location.city} - ${location.state}`,
    cep: `CEP: ${location.cep}`
  };
};

// Função removida - agendamento agora é gerenciado no profissional

// Função para formatar agrupamento de mensagem
const formatMessageGrouping = (value) => {
  if (value === 0 || value === '0') {
    return 'não agrupar';
  }
  if (value) {
    return `${value} segundos`;
  }
  return '25 segundos'; // valor padrão
};

// Função para converter porcentagem de aleatoriedade de áudio em texto
const getAudioRandomnessLevel = (value) => {
  if (value === 0) return 'Sem áudio';
  if (value <= 20) return 'Baixa';
  if (value <= 40) return 'Mediana';
  if (value <= 70) return 'Moderada';
  return 'Alta';
};

// Função para obter descrição da probabilidade de áudio
const getAudioRandomnessDescription = (value) => {
  if (value === 0) return 'O agente nunca enviará respostas em áudio.';
  if (value <= 20) return 'Raramente o agente enviará respostas em áudio.';
  if (value <= 40) return 'Ocasionalmente o agente enviará respostas em áudio.';
  if (value <= 70) return 'Frequentemente o agente enviará respostas em áudio.';
  return 'O agente enviará respostas em áudio na maioria das vezes.';
};

// Carregar dados do agente
const fetchAgent = async () => {
  isLoading.value = true;
  try {
    const authHeaders = getAuthHeaders();
    
    const response = await fetch(`/api/v1/agents_ai/${agentId.value}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders
      },
      credentials: 'include'
    });

    if (!response.ok) {
      // Se for 404 (agente não encontrado) ou 403 (não autorizado), redirecionar para lista
      if (response.status === 404 || response.status === 403) {
        console.error('Agente não encontrado ou não autorizado:', response.status);
        router.push({ name: 'agentis_list' });
        return;
      }
      throw new Error(`Erro ao buscar agente: ${response.status}`);
    }

    const data = await response.json();
    
    if (data && data.agent) {
      initializeAgentData(data.agent);
    } else {
      // Se não há dados do agente, redirecionar para lista
      console.error('Dados do agente não encontrados na resposta');
      router.push({ name: 'agentis_list' });
      return;
    }
  } catch (error) {
    console.error('Erro ao carregar agente:', error);
    // Tentar via store como fallback
    try {
      await store.dispatch('agents/get', agentId.value);
      const agent = store.getters['agents/getAgent'](agentId.value);
      if (agent) {
        initializeAgentData(agent);
      } else {
        // Se não encontrou no store também, redirecionar para lista
        console.error('Agente não encontrado no store');
        router.push({ name: 'agentis_list' });
        return;
      }
    } catch (storeError) {
      console.error('Erro ao carregar agente via store:', storeError);
      // Se falhou em ambos os métodos, redirecionar para lista
      router.push({ name: 'agentis_list' });
      return;
    }
  } finally {
    isLoading.value = false;
  }
};

// Inicializar dados do agente
const initializeAgentData = (agent) => {
  if (!agent) return;

  agentData.id = agent.id || null;
  agentData.name = agent.name || 'Agente';
  agentData.voice = agent.voice || 'female';
  agentData.createdAt = agent.created_at || null;
  agentData.updatedAt = agent.updated_at || null;
  
  // Parse company_data if it's a string
  let companyData = agent.company_data || {};
  if (typeof companyData === 'string') {
    try {
      companyData = JSON.parse(companyData);
    } catch (e) {
      companyData = {};
    }
  }
  
  agentData.company = {
    name: companyData.name || '',
    website: companyData.website || '',
    description: companyData.description || '',
    role: companyData.role || '',
    cnpj: companyData.cnpj || '',
    locations: Array.isArray(companyData.locations) ? [...companyData.locations] : [],
    messageGrouping: companyData.messageGrouping !== undefined ? companyData.messageGrouping : 25,
    // schedulingSettings removido - gerenciado no profissional
    additionalInfo: Array.isArray(companyData.additionalInfo) ? [...companyData.additionalInfo] : [],
    support: {
      phone: companyData.support?.phone || '',
      email: companyData.support?.email || '',
      faqLink: companyData.support?.faqLink || ''
    },
    policies: Array.isArray(companyData.policies) ? [...companyData.policies] : [],
    tagFields: Array.isArray(companyData.tagFields) ? [...companyData.tagFields] : [],
    teamFields: Array.isArray(companyData.teamFields) ? [...companyData.teamFields] : [],
    humanRedirectConditions: companyData.humanRedirectConditions || '',
    humanAvailabilityMessagingEnabled: companyData.humanAvailabilityMessagingEnabled === true,
    humanAvailabilityHours: companyData.humanAvailabilityHours || null,
    audioEnabled: companyData.audioEnabled !== undefined ? companyData.audioEnabled : true,
    randomness: companyData.randomness !== undefined ? companyData.randomness : 33,
    customImageInstructionsEnabled: companyData.customImageInstructionsEnabled !== undefined ? companyData.customImageInstructionsEnabled : false,
    imagePrompt: companyData.imagePrompt || ''
  };
  
  // Processar customer_data (que contém os Atributos do Usuário)
  let customerFields = agent.customer_data || [];
  if (typeof customerFields === 'string') {
    try {
      customerFields = JSON.parse(customerFields);
    } catch (e) {
      customerFields = [];
    }
  }
  
  // Armazenar em userFields
  agentData.userFields = Array.isArray(customerFields) 
    ? [...customerFields] 
    : [];
  
  agentData.personalityId = agent.personality || null;
  agentData.conversationFlowId = agent.flow || null;

  // Carregar configuração do carrinho de compras (apenas para fluxo de vendas)
  agentData.company.shoppingCartEnabled = companyData.shoppingCartEnabled === true;

  // Inicializar selectedInboxes vazio - será carregado após loadInboxes
  agentData.selectedInboxes = [];
  
  // Tratamento especial para additionalInfo no formato antigo (dentro de support)
  if (companyData.support && Array.isArray(companyData.support.additionalInfo) && companyData.support.additionalInfo.length > 0) {
    agentData.company.additionalInfo = [...companyData.support.additionalInfo];
    delete companyData.support.additionalInfo;
  }
};

const navigateBack = () => {
  router.push({ name: 'agentis_list' });
};

// Temporariamente comentado - função original de navegação para configurações avançadas
/*
const navigateToAdvancedSettings = () => {
  if (!agentData.id) {
    return;
  }

  try {
    // Fazer uma cópia profunda dos dados da empresa para garantir formato correto
    const companyDataCopy = JSON.parse(JSON.stringify(agentData.company));
    
    // Garantir que additionalInfo esteja no nível correto (fora de support)
    if (companyDataCopy.support && companyDataCopy.support.additionalInfo) {
      if (!companyDataCopy.additionalInfo) {
        companyDataCopy.additionalInfo = [];
      }
      companyDataCopy.additionalInfo = [...companyDataCopy.support.additionalInfo];
      delete companyDataCopy.support.additionalInfo;
    }

    // Definir valores padrão para áudio se não existirem
    if (companyDataCopy.audioEnabled === undefined) {
      companyDataCopy.audioEnabled = true;
    }
    if (companyDataCopy.randomness === undefined) {
      companyDataCopy.randomness = 40;
    }
    // Definir valores padrão para instruções customizadas de imagem
    if (companyDataCopy.customImageInstructionsEnabled === undefined) {
      companyDataCopy.customImageInstructionsEnabled = false;
    }
    if (companyDataCopy.imagePrompt === undefined) {
      companyDataCopy.imagePrompt = '';
    }

    // Preparar os dados para edição avançada
    const advancedEditData = {
      id: agentData.id,
      name: agentData.name,
      voice: agentData.voice,
      company_data: companyDataCopy,
      user_fields: Array.isArray(agentData.userFields) ? agentData.userFields : [],
      personality: agentData.personalityId,
      flow: agentData.conversationFlowId,
      additionalInfo: Array.isArray(companyDataCopy.additionalInfo) ? companyDataCopy.additionalInfo : [],
      tagFields: Array.isArray(companyDataCopy.tagFields) ? companyDataCopy.tagFields : [],
      teamFields: Array.isArray(companyDataCopy.teamFields) ? companyDataCopy.teamFields : [],
      audioEnabled: companyDataCopy.audioEnabled,
      randomness: companyDataCopy.randomness,
      customImageInstructionsEnabled: companyDataCopy.customImageInstructionsEnabled,
      imagePrompt: companyDataCopy.imagePrompt
    };
    
    // Armazenar os dados no sessionStorage
    sessionStorage.setItem('advancedEditingAgent', JSON.stringify(advancedEditData));
    
    // Navegar para a página de configurações avançadas
    router.push({
      name: 'agentis_advanced_settings',
      params: { agentId: agentData.id }
    });
  } catch (error) {
  }
};
*/

// Temporariamente redirecionando para editar agente
const navigateToAdvancedSettings = () => {
  navigateToEdit();
};

const navigateToEdit = () => {
  if (!agentData.id) {
    return;
  }

  try {
    // Fazer uma cópia profunda dos dados da empresa para garantir formato correto
    const companyDataCopy = JSON.parse(JSON.stringify(agentData.company));
    
    // Garantir que additionalInfo esteja no nível correto (fora de support)
    if (companyDataCopy.support && companyDataCopy.support.additionalInfo) {
      if (!companyDataCopy.additionalInfo) {
        companyDataCopy.additionalInfo = [];
      }
      companyDataCopy.additionalInfo = [...companyDataCopy.support.additionalInfo];
      delete companyDataCopy.support.additionalInfo;
    }

    // Definir valores padrão para áudio se não existirem
    if (companyDataCopy.audioEnabled === undefined) {
      companyDataCopy.audioEnabled = true;
    }
    if (companyDataCopy.randomness === undefined) {
      companyDataCopy.randomness = 40;
    }
    // Definir valores padrão para instruções customizadas de imagem
    if (companyDataCopy.customImageInstructionsEnabled === undefined) {
      companyDataCopy.customImageInstructionsEnabled = false;
    }
    if (companyDataCopy.imagePrompt === undefined) {
      companyDataCopy.imagePrompt = '';
    }

    // Adicionar campo do carrinho de compras

    // Preparar os dados para edição
    const editData = {
      id: agentData.id,
      name: agentData.name,
      voice: agentData.voice,
      company_data: companyDataCopy,
      user_fields: Array.isArray(agentData.userFields) ? agentData.userFields : [],
      personality: agentData.personalityId,
      flow: agentData.conversationFlowId,
      additionalInfo: Array.isArray(companyDataCopy.additionalInfo) ? companyDataCopy.additionalInfo : [],
      tagFields: Array.isArray(companyDataCopy.tagFields) ? companyDataCopy.tagFields : [],
      teamFields: Array.isArray(companyDataCopy.teamFields) ? companyDataCopy.teamFields : [],
      audioEnabled: companyDataCopy.audioEnabled,
      randomness: companyDataCopy.randomness,
      customImageInstructionsEnabled: companyDataCopy.customImageInstructionsEnabled,
      imagePrompt: companyDataCopy.imagePrompt
    };
    
    // Armazenar os dados no sessionStorage
    sessionStorage.setItem('editingAgent', JSON.stringify(editData));
    
    // Navegar para a página de criação com o modo de edição
    router.push({
      name: 'agentis_create',
      query: { mode: 'edit', id: agentData.id }
    });
  } catch (error) {
  }
};


// Carregar inboxes disponíveis
const loadInboxes = async () => {
  try {
    loadingInboxes.value = true;
    
    // Get data from Vuex store (already loaded by App.vue)
    const inboxMetadata = store.getters['inboxMetadata/getFormattedInboxes'];
    
    // Filter for connected inboxes only
    const connectedInboxes = (inboxMetadata.results || []).filter(inbox => {
      // Check if any instance has connectionStatus === 2
      return inbox.instances && inbox.instances.some(instance => 
        instance.connectionStatus === 2
      );
    });
    
    inboxOptions.value = connectedInboxes;
    
    // Após carregar os inboxes, verificar quais já têm este agente
    await loadAgentInboxes();
  } catch (err) {
    inboxOptions.value = [];
  } finally {
    loadingInboxes.value = false;
  }
};

// Função para carregar os inboxes que já têm este agente selecionado
const loadAgentInboxes = async () => {
  if (!agentData.id || inboxOptions.value.length === 0) return;
  
  try {
    const headers = getAuthHeaders();
    const selectedInboxIds = [];
    
    // Para cada inbox, verificar se tem este agente
    for (const inbox of inboxOptions.value) {
      try {
        const response = await wootAPI.get(
          `/api/v1/inboxmeta/edit`,
          {
            params: { inbox_id: inbox.inbox_id },
            headers
          }
        );
        
        // Verificar se o agente selecionado é este agente específico
        // Por enquanto, como a API retorna apenas o nome, vamos usar uma combinação de nome + ID
        // Idealmente a API deveria retornar o ID do agente
        if (response.data.selected_agent === agentData.name) {
          // TODO: Quando a API retornar o ID do agente, usar:
          // if (response.data.selected_agent_id === agentData.id) {
          selectedInboxIds.push(String(inbox.inbox_id));
        }
      } catch (err) {
      }
    }
    
    // Atualizar os inboxes selecionados
    agentData.selectedInboxes = selectedInboxIds;
  } catch (err) {
  }
};

// Função para alternar dropdown de inboxes
const toggleInboxDropdown = (event) => {
  showInboxDropdown.value = !showInboxDropdown.value;
  
  if (showInboxDropdown.value) {
    dropdownButton.value = event.currentTarget;
    updateDropdownPosition(event.currentTarget);
    
    if (inboxOptions.value.length === 0) {
      loadInboxes(); // Tentar carregar novamente se não houver inboxes
    }
  } else {
    dropdownButton.value = null;
  }
};

// Função para atualizar posição do dropdown
const updateDropdownPosition = (button) => {
  if (!button) return;
  
  const rect = button.getBoundingClientRect();
  const scrollContainer = document.querySelector('.custom-scrollbar');
  const containerRect = scrollContainer ? scrollContainer.getBoundingClientRect() : { top: 0, left: 0 };
  
  dropdownPosition.value = {
    top: rect.bottom + 4, // 4px gap
    width: rect.width // Adicionar largura do botão
  };
};

// Função para verificar se inbox está selecionado
const isInboxSelected = (inboxId) => {
  return agentData.selectedInboxes.includes(inboxId);
};

// Função para alternar seleção de inbox
const toggleInboxSelection = (inboxId) => {
  const currentSelection = [...agentData.selectedInboxes];
  const inboxIndex = currentSelection.indexOf(inboxId);
  
  if (inboxIndex === -1) {
    // Adicionar inbox à seleção
    currentSelection.push(inboxId);
  } else {
    // Remover inbox da seleção
    currentSelection.splice(inboxIndex, 1);
  }
  
  agentData.selectedInboxes = currentSelection;
};

// Função para obter ícone do tipo de inbox
const getInboxTypeIcon = (type) => {
  switch (type?.toLowerCase()) {
    case 'whatsapp':
      return 'i-lucide-message-circle';
    case 'facebook':
      return 'i-lucide-facebook';
    case 'twitter':
      return 'i-lucide-twitter';
    case 'email':
      return 'i-lucide-mail';
    case 'web':
      return 'i-lucide-globe';
    case 'telegram':
      return 'i-lucide-send';
    default:
      return 'i-lucide-message-square';
  }
};

// Computed para inboxes agrupados
const groupedInboxOptions = computed(() => {
  const result = [];
  
  const inboxes = inboxOptions.value || [];
  
  const whatsappEvolution = [];
  const whatsappOfficial = [];
  const otherChannels = [];
  
  inboxes.forEach(inbox => {
    const name = (inbox.inbox_name || inbox.name || '').toLowerCase();
    
    if (inbox.integration_type === 'official' || 
        name.includes('api oficial') ||
        name.includes('cloud') ||
        name.includes('official')) {
      whatsappOfficial.push({...inbox, integration_type: 'official'});
    }
    else if (inbox.integration_type === 'evolution' || name.includes('evolution') || name.includes('unofficial')) {
      whatsappEvolution.push({...inbox, integration_type: 'evolution'});
    }
    else {
      otherChannels.push(inbox);
    }
  });
  
  if (whatsappEvolution.length > 0) {
    result.push({ type: 'header', label: 'WHATSAPP NÃO OFICIAL' });
    result.push(...whatsappEvolution);
  }
  
  if (whatsappOfficial.length > 0) {
    result.push({ type: 'header', label: 'WHATSAPP API OFICIAL' });
    result.push(...whatsappOfficial);
  }
  
  if (otherChannels.length > 0) {
    result.push({ type: 'header', label: 'OUTROS CANAIS' });
    result.push(...otherChannels);
  }
  
  return result;
});

// Funções para display dos inboxes selecionados
const getInboxDisplayName = (inbox) => {
  // First try to get display_name from store
  const displayName = store.getters['inboxMetadata/getInboxDisplayName'](inbox.inbox_id || inbox.id);
  if (displayName) return displayName;
  
  // Fallback to inbox_name or name
  return inbox.inbox_name || inbox.name || `Inbox ${inbox.inbox_id || inbox.id}`;
};

const getSelectedInboxesDisplayText = () => {
  const selected = agentData.selectedInboxes;
  
  if (selected.length === 0) {
    return 'Nenhum inbox selecionado';
  } else if (selected.length === 1) {
    const inbox = inboxOptions.value.find(inbox => 
      String(inbox.inbox_id) === selected[0]
    );
    return inbox ? getInboxDisplayName(inbox) : 'Inbox desconhecido';
  } else {
    return `${selected.length} inboxes selecionados`;
  }
};

const getSelectedInboxesDisplayIcon = () => {
  const selected = agentData.selectedInboxes;
  
  if (selected.length === 0) {
    return 'i-lucide-inbox';
  } else if (selected.length === 1) {
    const inbox = inboxOptions.value.find(inbox => 
      String(inbox.inbox_id) === selected[0]
    );
    return inbox ? getInboxTypeIcon(inbox.channel_type) : 'i-lucide-inbox';
  } else {
    return 'i-lucide-layers';
  }
};

const getSelectedInboxesDisplayClasses = () => {
  const selected = agentData.selectedInboxes;
  
  if (selected.length === 0) {
    return 'bg-n-slate-2 text-n-slate-11';
  } else if (selected.length === 1) {
    const inbox = inboxOptions.value.find(inbox => 
      String(inbox.inbox_id) === selected[0]
    );
    if (!inbox) return 'bg-n-slate-2 text-n-slate-11';
    
    if (inbox.integration_type === 'official') {
      return 'bg-grass-300 text-grass-800 dark:bg-grass-200';
    } else if (inbox.integration_type === 'evolution') {
      return 'bg-n-amber-2 text-n-amber-11 dark:bg-n-amber-3 dark:text-n-amber-11';
    } else {
      return 'bg-n-slate-2 text-n-slate-11';
    }
  } else {
    return 'bg-n-amber-2 text-n-amber-11';
  }
};

// Função para salvar inboxes selecionados
const saveSelectedInboxes = async () => {
  if (!agentData.name || !agentData.id) return;
  
  try {
    const headers = getAuthHeaders();
    
    // Primeiro, remover o agente de TODOS os inboxes
    const removePromises = inboxOptions.value.map(async (inbox) => {
      try {
        // Verificar se este inbox tem este agente
        const response = await wootAPI.get(
          `/api/v1/inboxmeta/edit`,
          {
            params: { inbox_id: inbox.inbox_id },
            headers
          }
        );
        
        // Se tem este agente, remover
        if (response.data.selected_agent === agentData.name) {
          await wootAPI.post(
            `/api/v1/inboxmeta/update_meta`,
            {
              agent: '', // String vazia para remover
              inbox_id: parseInt(inbox.inbox_id),
            },
            { headers }
          );
        }
      } catch (err) {
      }
    });
    
    await Promise.all(removePromises);
    
    // Agora, adicionar o agente apenas aos inboxes selecionados.
    const savePromises = agentData.selectedInboxes.map(async (inboxId) => {
      try {
        await wootAPI.post(
          `/api/v1/inboxmeta/update_meta`,
          {
            agent: agentData.name,
            inbox_id: parseInt(inboxId),
          },
          { headers }
        );
        return { success: true, inboxId };
      } catch (err) {
        return { success: false, inboxId, error: err };
      }
    });
    
    const results = await Promise.all(savePromises);
    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;
    
    showInboxDropdown.value = false;
    
    if (successCount > 0 && failCount === 0) {
      useAlert(`Agente ${agentData.name} atribuído a ${successCount} inbox${successCount > 1 ? 'es' : ''}!`);
    } else if (successCount > 0 && failCount > 0) {
      useAlert(`Agente atribuído a ${successCount} inbox${successCount > 1 ? 'es' : ''}. ${failCount} falhou.`, 'warning');
    } else if (agentData.selectedInboxes.length === 0) {
      useAlert(`Agente ${agentData.name} removido de todos os inboxes`);
    } else {
      useAlert('Erro ao atribuir agente aos inboxes', 'error');
    }
    
    // Emitir evento para recarregar nomes dos agentes (como no EditInboxModal)
    emitter.emit('reload_agent_names');
  } catch (error) {
    useAlert('Erro ao salvar inboxes', 'error');
  }
};

// Função para detectar cliques fora do dropdown
const handleClickOutside = (event) => {
  if (!event.target.closest('.inbox-dropdown') && showInboxDropdown.value) {
    showInboxDropdown.value = false;
  }
};

// Referência para o botão do dropdown
const dropdownButton = ref(null);

// Função para lidar com scroll e reposicionar dropdown
const handleScroll = () => {
  if (showInboxDropdown.value && dropdownButton.value) {
    updateDropdownPosition(dropdownButton.value);
  }
};

// Carregar dados quando o componente for montado
onMounted(async () => {
  await checkDebugMode();
  await checkGoogleCalendarConnection();
  await fetchAgent();
  await loadInboxes();
  
  document.addEventListener('click', handleClickOutside);
  
  // Adicionar listener de scroll para reposicionar dropdown
  if (scrollContainer.value) {
    scrollContainer.value.addEventListener('scroll', handleScroll);
  }
  window.addEventListener('scroll', handleScroll);
  window.addEventListener('resize', handleScroll);

  nextTick(() => {
    checkScrollbar();
    
    // Observar mudanças no tamanho do conteúdo
    if (scrollContainer.value) {
      const resizeObserver = new ResizeObserver(() => {
        checkScrollbar();
      });
      resizeObserver.observe(scrollContainer.value);
      
      // Limpar observer ao desmontar
      onUnmounted(() => {
        resizeObserver.disconnect();
        document.removeEventListener('click', handleClickOutside);
        if (scrollContainer.value) {
          scrollContainer.value.removeEventListener('scroll', handleScroll);
        }
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      });
    }
  });
});

// Função para redirecionar para configurações de calendário
const goToCalendarSettings = () => {
  router.push({
    name: 'calendar_settings',
    params: { accountId: accountId.value }
  });
};

// Função para alternar expansão de item
const toggleItemExpansion = (type, index) => {
  const itemIndex = expandedItems.value[type].indexOf(index);
  if (itemIndex === -1) {
    expandedItems.value[type].push(index);
  } else {
    expandedItems.value[type].splice(itemIndex, 1);
  }
};

// Função para verificar se item está expandido
const isItemExpanded = (type, index) => {
  return expandedItems.value[type].includes(index);
};

// Função para obter preview do conteúdo
const getContentPreview = (content, maxLength = 80) => {
  if (!content) return '';
  const plainText = content.replace(/\n/g, ' ').trim();
  if (plainText.length <= maxLength) return plainText;
  return plainText.substring(0, maxLength).trim() + '...';
};

// Função otimizada para scroll para o topo
const scrollToTop = async () => {
  await nextTick();
  
  // Usa apenas o container principal com scroll
  if (scrollContainer.value) {
    scrollContainer.value.scrollTo({ top: 0, behavior: 'instant' });
  } else {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }
};

// Ref para o elemento com scroll
const scrollContainer = ref(null);

// Função para verificar e ajustar padding quando há scrollbar
const checkScrollbar = () => {
  if (scrollContainer.value) {
    // Força um reflow para garantir que as medidas estejam corretas
    scrollContainer.value.offsetHeight;
    
    const hasScrollbar = scrollContainer.value.scrollHeight > scrollContainer.value.clientHeight;
    if (hasScrollbar) {
      scrollContainer.value.style.paddingRight = '0px';
    } else {
      scrollContainer.value.style.paddingRight = '10px';
    }
  }
};

// Função para formatar data
const formatDate = (dateString) => {
  if (!dateString) return 'Não disponível';
  
  try {
    const date = new Date(dateString);
    const options = { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return date.toLocaleDateString('pt-BR', options);
  } catch (error) {
    return 'Data inválida';
  }
};

</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-background">
    <!-- Header -->
    <header class="flex justify-between items-center h-14 min-h-[3.5rem] px-4 py-2 bg-n-background border-b border-n-weak pl-6 flex-shrink-0 z-10">
      <div class="flex justify- items-center gap-2">
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-arrow-left"
          @click="navigateBack"
        />
        <h1 class="text-2xl font-semibold font-interDisplay tracking-[0.3px] text-slate-900 dark:text-slate-25">
          {{ props.isDuplicated ? 'Detalhes do Agente Duplicado' : 'Detalhes do Agente' }}
        </h1>
      </div>
      <div class="flex gap-3 items-center">
        <Button
          v-if="isDebugMode"
          variant="outline"
          color="amber"
          size="sm"
          icon="i-lucide-terminal"
          @click="toggleDebugConsole"
        >
          {{ showDebugConsole ? 'Debug' : 'Ver JSON' }}
        </Button>
        <Button
          variant="solid"
          color="blue"
          size="md"
          icon="i-lucide-edit-3"
          @click="navigateToEdit"
        >
          Editar Agente
        </Button>
      </div>
    </header>

    <!-- Content -->
    <div ref="scrollContainer" class="flex-1 overflow-y-auto custom-scrollbar padding-no-scroll">
      <div v-if="isLoading || !agentData.id" class="flex items-center justify-center flex-grow p-10">
        <Spinner />
      </div>
      
      <div v-else class="flex flex-col gap-6 p-6 max-w-7xl mx-auto">
        <!-- Header com informações principais -->
        <div class="bg-gradient-to-r p-6 border-b border-n-weak items-center flex flex-col w-full">
          <div class="flex flex-col lg:flex-row lg:items-end items-center lg:justify-between gap-6 w-full max-w-7xl">
            <div class="flex items-center gap-5">
              <div class="bg-n-brand h-16 w-16 rounded-xl flex items-center justify-center text-white shadow-lg">
                <div class="i-lucide-bot h-8 w-8"></div>
              </div>
              <div class="flex-1">
                <h3 class="text-2xl font-bold text-n-slate-12 mb-1">{{ agentData.name }}</h3>
                <div class="flex flex-wrap gap-2 mb-2">
                  <span class="bg-n-brand text-white px-3 py-1 rounded-full text-xs ">
                    {{ voiceTypes[agentData.voice] || agentData.voice }}
                  </span>
                  <span class="bg-n-slate-3 text-n-slate-11 px-3 py-1 rounded-full text-xs ">
                    {{ personalityTypes[agentData.personalityId] || 'Personalidade não definida' }}
                  </span>
                  <span class="bg-n-slate-3 text-n-slate-11 px-3 py-1 rounded-full text-xs ">
                    {{ flowTypes[agentData.conversationFlowId] || 'Fluxo não definido' }}
                  </span>
                </div>
                <div class="flex flex-wrap gap-4 text-xs text-n-slate-10">
                  <span class="flex items-center gap-1">
                    <i class="i-lucide-calendar-plus text-n-slate-9"></i>
                    Criado: {{ formatDate(agentData.createdAt) }}
                  </span>
                  <span class="flex items-center gap-1">
                    <i class="i-lucide-calendar-check text-n-slate-9"></i>
                    Atualizado: {{ formatDate(agentData.updatedAt) }}
                  </span>
                </div>
              </div>
            </div>

            <div class="gap-3 flex flex-col">
              <!-- Verificação de disponibilidade da agenda -->
              <div v-if="agentData.conversationFlowId === 3" class="flex flex-row-reverse">
                <!-- Agenda vinculada -->
                <span v-if="scheduleStatus.isCalendarConnected" class="inline-flex items-center gap-2 px-3 py-1.5 bg-n-teal-2 text-n-teal-11 rounded-full text-sm">
                  <i class="i-lucide-calendar-check text-n-teal-9"></i>
                  Agenda vinculada
                </span>
                <!-- Agenda não vinculada -->
                <router-link 
                  v-else
                  :to="`/app/accounts/${accountId}/settings/integrations/googleCalendar`"
                  class="inline-flex items-center gap-2 px-3 py-1.5 bg-n-amber-2 text-n-amber-11 rounded-full text-sm hover:bg-n-amber-3 transition-colors cursor-pointer no-underline"
                >
                  <i class="i-lucide-calendar-x text-n-amber-9"></i>
                  Agenda não vinculada - Clique para configurar
                </router-link>
              </div>
              
              <!-- Dropdown de Seleção de Inboxes -->
              <div class="relative inbox-dropdown flex flex-row-reverse">
                <button
                  @click.stop="toggleInboxDropdown($event)"
                  class="flex items-center gap-2 px-3 py-2 border border-n-slate-4 rounded-lg text-sm bg-n-background text-n-slate-12 hover:bg-n-slate-1 focus:border-n-brand transition-colors border-n-slate-4-dark-custom"
                  type="button"
                  :disabled="loadingInboxes"
                >
                  <div 
                    class="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0"
                    :class="getSelectedInboxesDisplayClasses()"
                  >
                    <span class="i-lucide-message-square text-sm"></span>
                  </div>
                  <span class="truncate max-w-[200px]">
                    {{ getSelectedInboxesDisplayText() }}
                  </span>
                  <i v-if="!loadingInboxes" class="i-lucide-chevron-down ml-1 flex-shrink-0 transition-transform text-xs" :class="{ 'rotate-180': showInboxDropdown }"></i>
                  <i v-else class="i-lucide-loader animate-spin ml-1 flex-shrink-0 text-xs"></i>
                </button>
                
                <!-- Dropdown Options -->
                <div v-if="showInboxDropdown && !loadingInboxes" 
                    class="fixed border border-n-slate-3 rounded-md shadow-lg bg-n-slate-2 overflow-hidden mt-1"
                    :style="{
                      top: dropdownPosition.top + 'px',
                      minWidth: dropdownPosition.width + 'px',
                      maxHeight: '400px'
                    }"
                    @click.stop>
                  <!-- Grouped Inboxes -->
                  <div v-if="inboxOptions.length === 0" class="p-4 text-center text-n-slate-10">
                    Nenhum inbox disponível
                  </div>
                  <div v-else class="max-h-60 overflow-y-auto">
                    <template v-for="(item, index) in groupedInboxOptions" :key="index">
                      <!-- Group Header -->
                      <div v-if="item.type === 'header'"
                          class="px-3 py-1 text-xs font-semibold bg-n-slate-1 dark:bg-n-slate-3 text-n-slate-10 border-y border-n-slate-3 sticky top-0 z-10">
                        {{ item.label }}
                      </div>
                      
                      <!-- Inbox Option -->
                      <div v-else
                          class="p-2 hover:bg-n-slate-1 dark:hover:bg-n-slate-3 cursor-pointer"
                          @click.stop="toggleInboxSelection(String(item.inbox_id))">
                        <div class="flex items-center">
                          <input type="checkbox" 
                                :checked="isInboxSelected(String(item.inbox_id))" 
                                @click.stop
                                @change.stop="toggleInboxSelection(String(item.inbox_id))"
                                class="mr-3">
                          <div class="w-8 h-8 flex items-center justify-center rounded-full mr-3"
                              :class="{
                                'bg-n-amber-2 text-n-amber-11 dark:bg-n-amber-3 dark:text-n-amber-11': 
                                  item.integration_type === 'evolution',
                                'bg-grass-300 text-grass-800 dark:bg-grass-200': 
                                  item.integration_type === 'official',
                                'bg-n-slate-2 text-n-slate-11': 
                                  !item.integration_type
                              }">
                            <i :class="getInboxTypeIcon(item.channel_type)"></i>
                          </div>
                          
                          <div>
                            <div class="text-sm font-medium">
                              {{ getInboxDisplayName(item) }}
                            </div>
                            <div v-if="item.integration_type === 'official'" class="text-xs text-grass-800 dark:text-grass-800">
                              API Oficial
                            </div>
                            <div v-else-if="item.integration_type === 'evolution'" class="text-xs text-n-amber-11 dark:text-n-amber-11">
                              Não Oficial
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                  
                  <!-- Footer com botão de salvar -->
                  <div class="border-t border-n-slate-3 p-2 bg-n-slate-1 dark:bg-n-slate-3">
                    <Button
                      label="Salvar Seleção"
                      size="sm"
                      color="blue"
                      class="w-full"
                      @click="saveSelectedInboxes"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Cards de informações principais -->
        <div class="p-6 flex flex-col items-center w-full max-w-7xl">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 w-full">
            <div class="bg-n-slate-1 p-4 rounded-xl border border-n-slate-3 hover:border-n-brand transition-colors">
              <div class="flex items-center gap-3 mb-2">
                <div class="i-lucide-building-2 text-n-brand"></div>
                <span class="text-n-slate-11 text-sm ">Empresa</span>
              </div>
              <p class="font-semibold text-n-slate-12 truncate" :title="agentData.company.name">
                {{ agentData.company.name || 'Não definida' }}
              </p>
            </div>
            
            <div class="bg-n-slate-1 p-4 rounded-xl border border-n-slate-3 hover:border-n-brand transition-colors">
              <div class="flex items-center gap-3 mb-2">
                <div class="i-lucide-globe text-n-brand"></div>
                <span class="text-n-slate-11 text-sm ">Website</span>
              </div>
              <p class="font-semibold text-n-slate-12 truncate" :title="agentData.company.website">
                {{ agentData.company.website || 'Não definido' }}
              </p>
            </div>
            
            <div class="bg-n-slate-1 p-4 rounded-xl border border-n-slate-3 hover:border-n-brand transition-colors">
              <div class="flex items-center gap-3 mb-2">
                <div class="i-lucide-users text-n-brand"></div>
                <span class="text-n-slate-11 text-sm ">Atributos do Usuário</span>
              </div>
              <p class="font-semibold text-n-slate-12">{{ agentData.userFields.length }} atributos</p>
            </div>
            
            <div class="bg-n-slate-1 p-4 rounded-xl border border-n-slate-3 hover:border-n-brand transition-colors">
              <div class="flex items-center gap-3 mb-2">
                <div class="i-lucide-clock text-n-brand"></div>
                <span class="text-n-slate-11 text-sm ">Agrupamento</span>
              </div>
              <p class="font-semibold text-n-slate-12">{{ formatMessageGrouping(agentData.company.messageGrouping) }}</p>
            </div>
          </div>

          <!-- Abas de navegação -->
          <div class="flex border-b mb-6 justify-between border-n-brand w-full">
            <button 
              v-for="tab in tabs" 
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="[
                'px-4 py-3 text-sm transition-colors rounded-b-none border-b-0',
                activeTab === tab.id 
                  ? 'border-n-brand text-n-brand border-n-brand-dark-custom' 
                  : 'border-transparent text-n-slate-11 hover:text-n-slate-12 hover:border-n-slate-6'
              ]"
            >
              <div class="flex items-center gap-2">
                <div :class="tab.icon"></div>
                {{ tab.label }}
              </div>
            </button>
          </div>

          <!-- Conteúdo das abas -->
          <div v-if="activeTab === 'company'" class="tab-content w-full max-w-7xl">
            <!-- Informações da Empresa -->
            <div class="space-y-6">
              <!-- Descrição destacada -->
              <div v-if="agentData.company.description" class="bg-n-slate-1 p-4 rounded-xl border border-n-slate-3 hover:border-n-brand transition-colors">
                <div class="flex items-start gap-3">
                  <div class="i-lucide-info text-n-brand mt-1"></div>
                  <div>
                    <h4 class="font-semibold text-n-slate-12 mb-2">Descrição da Empresa</h4>
                    <p class="text-n-slate-11 whitespace-pre-wrap break-words leading-relaxed">
                      {{ agentData.company.description }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Grid de informações básicas -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="bg-n-slate-1 p-4 rounded-xl border border-n-slate-3 hover:border-n-brand transition-colors">
                  <div class="flex items-center gap-3 mb-3">
                    <div class="i-lucide-building-2 text-n-brand"></div>
                    <span class="text-n-slate-11 text-sm ">Nome da Empresa</span>
                  </div>
                  <p class="font-semibold text-n-slate-12">
                    {{ agentData.company.name || 'Não definido' }}
                  </p>
                </div>

                <div class="bg-n-slate-1 p-4 rounded-xl border border-n-slate-3 hover:border-n-brand transition-colors">
                  <div class="flex items-center gap-3 mb-3">
                    <div class="i-lucide-file-text text-n-brand"></div>
                    <span class="text-n-slate-11 text-sm ">CNPJ</span>
                  </div>
                  <p class="font-semibold text-n-slate-12">
                    {{ agentData.company.cnpj || 'Não definido' }}
                  </p>
                </div>

                <div class="bg-n-slate-1 p-4 rounded-xl border border-n-slate-3 hover:border-n-brand transition-colors">
                  <div class="flex items-center gap-3 mb-3">
                    <div class="i-lucide-globe text-n-brand"></div>
                    <span class="text-n-slate-11 text-sm ">Website</span>
                  </div>
                  <p class="font-semibold text-n-slate-12 break-all">
                    {{ agentData.company.website || 'Não definido' }}
                  </p>
                </div>

                <div class="bg-n-slate-1 p-4 rounded-xl border border-n-slate-3 hover:border-n-brand transition-colors">
                  <div class="flex items-center gap-3 mb-3">
                    <div class="i-lucide-user-check text-n-brand"></div>
                    <span class="text-n-slate-11 text-sm ">Função do Agente</span>
                  </div>
                  <p class="font-semibold text-n-slate-12 leading-relaxed break-words">
                    {{ agentData.company.role || 'Não definida' }}
                  </p>
                </div>
              </div>

              <!-- Informações de suporte -->
              <div class="bg-n-teal-1 border border-n-teal-3 p-6 rounded-xl">
                <h4 class="font-semibold text-n-slate-12 mb-4 flex items-center gap-2">
                  <div class="i-lucide-headphones text-n-teal-9"></div>
                  Informações de Suporte
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div class="bg-n-alpha-black2 p-4 rounded-lg">
                    <p class="text-n-slate-11 text-xs  mb-1">Email</p>
                    <p class=" text-n-slate-12 break-all">
                      {{ agentData.company.support.email || 'Não definido' }}
                    </p>
                  </div>
                  <div class="bg-n-alpha-black2 p-4 rounded-lg">
                    <p class="text-n-slate-11 text-xs  mb-1">Telefone</p>
                    <p class=" text-n-slate-12">
                      {{ agentData.company.support.phone || 'Não definido' }}
                    </p>
                  </div>
                  <div class="bg-n-alpha-black2 p-4 rounded-lg">
                    <p class="text-n-slate-11 text-xs  mb-1">FAQ</p>
                    <p class=" text-n-slate-12 break-all">
                      {{ agentData.company.support.faqLink || 'Não definido' }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Políticas da Empresa -->
              <div v-if="agentData.company.policies && agentData.company.policies.length > 0">
                <h4 class="font-semibold text-n-slate-12 mb-4 flex items-center gap-2">
                  <div class="i-lucide-shield-check text-n-ruby-9"></div>
                  Políticas da Empresa
                </h4>
                <div class="grid grid-cols-1 gap-4">
                  <div v-for="(policy, index) in agentData.company.policies" :key="`policy-${index}`" 
                       class="bg-n-slate-1 p-4 rounded-xl border border-n-slate-3 hover:border-n-brand transition-all duration-200 cursor-pointer"
                       @click="toggleItemExpansion('policies', index)">
                    <div class="flex items-start justify-between gap-3">
                      <div class="flex-1">
                        <h5 class="font-semibold text-n-slate-12 mb-2">{{ policy.name }}</h5>
                        <p v-if="!isItemExpanded('policies', index)" 
                           class="text-n-slate-11 leading-relaxed">
                          {{ getContentPreview(policy.value) }}
                        </p>
                        <p v-else 
                           class="text-n-slate-11 whitespace-pre-wrap break-words leading-relaxed animate-fadeIn">
                          {{ policy.value }}
                        </p>
                      </div>
                      <button type="button" class="flex-shrink-0 mt-1 text-n-slate-10 hover:text-n-slate-12 transition-colors">
                        <i :class="isItemExpanded('policies', index) ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" 
                           class="w-5 h-5"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-center py-12 bg-n-slate-1 rounded-xl border border-n-slate-3">
                <div class="i-lucide-shield-check text-n-slate-6 w-12 h-12 mx-auto mb-4"></div>
                <p class="text-n-slate-11 text-lg font-medium mb-2">Nenhuma política definida</p>
                <p class="text-n-slate-10 text-sm mb-4">Adicione políticas da empresa para orientar o atendimento do agente</p>
                <Button
                  variant="outline"
                  color="slate"
                  size="sm"
                  icon="i-lucide-edit-3"
                  @click="navigateToEdit"
                >
                  Editar Agente
                </Button>
              </div>
            </div>
          </div>

          <div v-if="activeTab === 'locations'" class="tab-content w-full max-w-7xl space-y-6">
            <!-- Localizações -->
            <div v-if="agentData.company.locations && agentData.company.locations.length > 0" 
                 class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div v-for="(location, index) in agentData.company.locations" :key="`location-${index}`" 
                   class="bg-n-slate-1 border border-n-slate-3 p-6 rounded-xl hover:border-n-brand transition-colors">
                <div class="flex items-center gap-3 mb-4">
                  <div class="bg-n-brand p-2.5 rounded-full w-10 h-10">
                    <div class="i-lucide-map-pin text-white w-5 h-5"></div>
                  </div>
                  <h4 class="font-semibold text-n-slate-12 text-lg">Endereço {{ index + 1 }}</h4>
                </div>
                <div class="space-y-3">
                  <div class="bg-n-alpha-black2 p-3 rounded-lg">
                    <p class="text-n-slate-11 text-xs  mb-1">Logradouro</p>
                    <p class="text-n-slate-12 ">{{ formatAddress(location).street }}</p>
                  </div>
                  <div class="bg-n-alpha-black2 p-3 rounded-lg">
                    <p class="text-n-slate-11 text-xs  mb-1">Bairro</p>
                    <p class="text-n-slate-12 ">{{ formatAddress(location).neighborhood }}</p>
                  </div>
                  <div class="bg-n-alpha-black2 p-3 rounded-lg">
                    <p class="text-n-slate-11 text-xs  mb-1">Cidade</p>
                    <p class="text-n-slate-12 ">{{ formatAddress(location).city }}</p>
                  </div>
                  <div class="bg-n-alpha-black2 p-3 rounded-lg">
                    <p class="text-n-slate-11 text-xs  mb-1">CEP</p>
                    <p class="text-n-slate-12 ">{{ formatAddress(location).cep }}</p>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-12 bg-n-slate-1 rounded-xl border border-n-slate-3">
              <div class="i-lucide-map-pin text-n-slate-6 w-12 h-12 mx-auto mb-4"></div>
              <p class="text-n-slate-11 text-lg font-medium mb-2">Nenhuma localização definida</p>
              <p class="text-n-slate-10 text-sm mb-4">Adicione endereços da empresa para melhorar o atendimento localizado</p>
              <Button
                variant="outline"
                color="slate"
                size="sm"
                icon="i-lucide-edit-3"
                @click="navigateToEdit"
              >
                Editar Agente
              </Button>
            </div>
          </div>

          <div v-if="activeTab === 'fields'" class="tab-content w-full max-w-7xl space-y-6">
            <!-- Atributos do Usuário -->
            <div v-if="agentData.userFields && agentData.userFields.length > 0" class="space-y-4">
              <div v-for="(field, index) in agentData.userFields" :key="`field-${index}`" 
                  class="bg-n-slate-1 border border-n-slate-3 p-6 rounded-xl hover:border-n-brand transition-colors">
                <div class="flex flex-col gap-4">
                  <div class="flex-1 flex justify-between">
                    <div class="flex items-center gap-3 mb-2">
                      <div class="i-lucide-user text-n-brand"></div>
                      <h4 class="font-semibold text-n-slate-12 text-lg">{{ field.name }}</h4>
                    </div>
                    <span class="bg-n-brand text-white px-4 py-2 rounded-lg text-sm ">
                      {{ field.required ? 'Obrigatório' : 'Opcional' }}
                    </span>
                  </div>
                    <p class="text-n-slate-11 leading-relaxed break-words">{{ field.description }}</p>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-12 bg-n-slate-1 rounded-xl border border-n-slate-3">
              <div class="i-lucide-user text-n-slate-6 w-12 h-12 mx-auto mb-4"></div>
              <p class="text-n-slate-11 text-lg font-medium mb-2">Nenhum campo de usuário definido</p>
              <p class="text-n-slate-10 text-sm mb-4">Personalize os atributos de coleta de informações do cliente</p>
              <Button
                variant="outline"
                color="slate"
                size="sm"
                icon="i-lucide-edit-3"
                @click="navigateToEdit"
              >
                Editar Agente
              </Button>
            </div>
          </div>

          <div v-if="activeTab === 'behavior'" class="tab-content w-full max-w-7xl space-y-6">
            <!-- Aba Comportamento -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <!-- Personalidade -->
              <div class="bg-n-slate-1 p-4 rounded-xl border border-n-slate-3 hover:border-n-brand transition-colors">
                <div class="flex items-center gap-3 mb-4">
                  <div class="i-lucide-brain text-n-brand"></div>
                  <h4 class="font-semibold text-n-slate-12">Personalidade</h4>
                </div>
                <h5 class="font-semibold text-n-slate-12 mb-2 text-lg">
                  {{ personalityTypes[agentData.personalityId] || 'Não definida' }}
                </h5>
                <p class="text-n-slate-11 leading-relaxed">
                  {{ agentData.personalityId === 1 ? 'Um estilo formal e profissional, ideal para atendimento corporativo.' : 
                     agentData.personalityId === 2 ? 'Um estilo casual e acolhedor, ótimo para marcas mais descontraídas.' : 
                     agentData.personalityId === 3 ? 'Um estilo focado em orientação e consultoria, ideal para serviços complexos.' : 
                     'Nenhuma personalidade selecionada.' }}
                </p>
              </div>
              
              <!-- Fluxo de Conversa -->
              <div class="bg-n-slate-1 p-4 rounded-xl border border-n-slate-3 hover:border-n-brand transition-colors">
                <div class="flex items-center gap-3 mb-4">
                  <div class="i-lucide-workflow text-n-brand"></div>
                  <h4 class="font-semibold text-n-slate-12">Fluxo de Conversa</h4>
                </div>
                <h5 class="font-semibold text-n-slate-12 mb-2 text-lg">
                  {{ flowTypes[agentData.conversationFlowId] || 'Não definido' }}
                </h5>
                <p class="text-n-slate-11 leading-relaxed">
                  {{ agentData.conversationFlowId === 1 ? 'Fluxo inicial para coletar e validar todas as informações do cliente antes de qualquer recomendação.' : 
                     agentData.conversationFlowId === 2 ? 'Fluxo focado em entender gostos e oferecer produtos conduzindo até a finalização da compra.' : 
                     agentData.conversationFlowId === 3 ? 'Fluxo de agendamento de serviços ou atendimentos e vendas de produtos.' : 
                     'Nenhum fluxo de conversa selecionado.' }}
                </p>
              </div>

              <!-- Carrinho de Compras (apenas para fluxo de vendas) -->
              <div v-if="agentData.conversationFlowId === 2" class="bg-n-slate-1 p-4 rounded-xl border border-n-slate-3 hover:border-n-brand transition-colors">
                <div class="flex items-center gap-3 mb-4">
                  <div class="i-lucide-shopping-cart text-n-brand"></div>
                  <h4 class="font-semibold text-n-slate-12">Carrinho de Compras</h4>
                </div>
                <div class="flex items-center justify-between">
                  <div>
                    <h5 class="font-semibold text-n-slate-12 mb-1">
                      Status do Carrinho
                    </h5>
                    <p class="text-n-slate-11">
                      {{ agentData.company.shoppingCartEnabled ? 'Carrinho habilitado para este agente' : 'Carrinho desabilitado para este agente' }}
                    </p>
                  </div>
                  <div :class="[
                    'px-3 py-1.5 rounded-lg font-medium text-sm',
                    agentData.company.shoppingCartEnabled
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-900/20 dark:text-gray-400'
                  ]">
                    {{ agentData.company.shoppingCartEnabled ? 'Ativo' : 'Inativo' }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Redirecionamento para Humanos -->
            <div v-if="agentData.company.humanRedirectConditions" class="bg-n-iris-1 border border-n-iris-3 p-6 rounded-xl">
              <div class="flex items-start gap-3">
                <div class="bg-n-iris-9 p-2.5 rounded-full flex-shrink-0 h-10 w-10 mt-1">
                  <div class="i-lucide-user-check text-white w-5 h-5"></div>
                </div>
                <div class="flex-1">
                  <h4 class="font-semibold text-n-slate-12 mb-3 flex items-center gap-2">
                    Redirecionamento para Humanos
                  </h4>
                  <p class="text-n-slate-11 whitespace-pre-wrap break-words leading-relaxed">
                    {{ agentData.company.humanRedirectConditions }}
                  </p>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-12 bg-n-slate-1 rounded-xl border border-n-slate-3">
              <div class="i-lucide-user-check text-n-slate-6 w-12 h-12 mx-auto mb-4"></div>
              <p class="text-n-slate-11 text-lg font-medium mb-2">Nenhuma condição específica de redirecionamento definida</p>
              <p class="text-n-slate-10 text-sm mb-4">Configure quando o agente deve transferir a conversa para um atendente humano</p>
              <Button
                variant="outline"
                color="slate"
                size="sm"
                icon="i-lucide-edit-3"
                @click="navigateToEdit"
              >
                Editar Agente
              </Button>
            </div>

            <!-- Mensagens de Disponibilidade Humana -->
            <div v-if="agentData.company.humanAvailabilityMessagingEnabled && agentData.company.humanAvailabilityHours" class="bg-n-teal-1 border border-n-teal-3 p-6 rounded-xl">
              <div class="flex items-start gap-3">
                <div class="bg-n-teal-9 p-2.5 rounded-full flex-shrink-0 h-10 w-10 mt-1">
                  <div class="i-lucide-message-circle text-white w-5 h-5"></div>
                </div>
                <div class="flex-1">
                  <h4 class="font-semibold text-n-slate-12 mb-4 flex items-center gap-2">
                    Mensagens de Disponibilidade Humana
                    <span class="text-xs px-2 py-1 bg-n-teal-2 text-n-teal-11 rounded-full font-medium">Configurado</span>
                  </h4>

                  <!-- Lista de Horários de Disponibilidade -->
                  <div class="mb-3">
                    <p class="text-sm text-n-slate-11 mb-3">
                      O agente informa sobre disponibilidade humana baseado nos seguintes horários:
                    </p>
                  </div>

                  <div class="space-y-2">
                    <div v-for="day in daysOfWeekDisplay" :key="day.key" class="flex items-center gap-3">
                      <div class="w-32">
                        <span class="text-sm font-medium text-n-slate-12">{{ day.label }}:</span>
                      </div>
                      <div v-if="agentData.company.humanAvailabilityHours[day.key] && agentData.company.humanAvailabilityHours[day.key].enabled" class="flex-1">
                        <div class="flex flex-wrap gap-2">
                          <span
                            v-for="(slot, index) in agentData.company.humanAvailabilityHours[day.key].timeSlots"
                            :key="index"
                            class="text-sm bg-n-teal-2 text-n-teal-11 px-2 py-1 rounded"
                          >
                            {{ slot.startTime }} - {{ slot.endTime }}
                          </span>
                        </div>
                      </div>
                      <div v-else class="text-sm text-n-slate-10 italic">
                        Sem expediente
                      </div>
                    </div>
                  </div>

                  <div class="mt-4 p-3 bg-n-teal-2 rounded-lg">
                    <p class="text-xs text-n-teal-11 flex items-start gap-1">
                      <span class="i-lucide-info w-3 h-3 mt-0.5 flex-shrink-0"></span>
                      <span>O agente usa esses horários para informar quando um atendente humano poderá entrar em contato. Por exemplo: se alguém conversar no domingo, o agente informará que "na segunda-feira um atendente entrará em contato".</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Seção de agendamento removida - agora gerenciada no profissional -->
          <div v-if="activeTab === 'schedules'" class="tab-content w-full max-w-7xl space-y-6">
            <div class="text-center py-12 bg-n-slate-1 rounded-xl border border-n-slate-3">
              <div class="i-lucide-calendar-clock text-n-slate-6 w-12 h-12 mx-auto mb-4"></div>
              <p class="text-n-slate-11 text-lg font-medium mb-2">Configurações de Agendamento</p>
              <p class="text-n-slate-10 text-sm mb-4">As configurações de horário agora são gerenciadas diretamente no cadastro do profissional</p>
              <Button
                variant="outline"
                color="slate"
                size="sm"
                icon="i-lucide-users"
                @click="() => {}"
              >
                Editar Agente
              </Button>
            </div>
          </div>

          <div v-if="activeTab === 'technical'" class="tab-content w-full max-w-7xl space-y-6">
            <!-- Aba Configurações Técnicas -->
            <!-- Configurações de Áudio -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <!-- Áudio Ativo -->
              <div class="bg-n-slate-1 p-4 rounded-xl border border-n-slate-3 hover:border-n-brand transition-colors">
                <div class="flex items-center gap-3 mb-4">
                  <div class="i-lucide-volume-2 text-n-brand"></div>
                  <h4 class="font-semibold text-n-slate-12">Áudio Ativo</h4>
                </div>
                <h5 class="font-semibold text-n-slate-12 mb-2 text-lg">
                  {{ agentData.company.audioEnabled ? 'Habilitado' : 'Desabilitado' }}
                </h5>
                <p class="text-n-slate-11 leading-relaxed">
                  {{ agentData.company.audioEnabled 
                    ? 'O agente pode responder com áudio nas conversas.' 
                    : 'O agente responderá apenas com texto.' }}
                </p>
              </div>

              <!-- Aleatoriedade de Áudio -->
              <div class="bg-n-slate-1 p-4 rounded-xl border border-n-slate-3 hover:border-n-brand transition-colors">
                <div class="flex items-center gap-3 mb-4">
                  <div class="i-lucide-shuffle text-n-brand"></div>
                  <h4 class="font-semibold text-n-slate-12">Probabilidade de Áudio</h4>
                </div>
                <h5 class="font-semibold text-n-slate-12 mb-2 text-lg">
                  {{ getAudioRandomnessLevel(agentData.company.randomness) }}
                </h5>
                <p class="text-n-slate-11 leading-relaxed">
                  {{ getAudioRandomnessDescription(agentData.company.randomness) }}
                </p>
              </div>
            </div>

            <!-- Instruções Personalizadas de Leitura de Imagem -->
            <div v-if="agentData.company.customImageInstructionsEnabled && agentData.company.imagePrompt" 
                 class="bg-n-iris-1 border border-n-iris-3 p-6 rounded-xl">
              <div class="flex items-start gap-3">
                <div class="bg-n-iris-9 p-2.5 rounded-full flex-shrink-0 h-10 w-10 mt-1">
                  <div class="i-lucide-image text-white w-5 h-5"></div>
                </div>
                <div class="flex-1">
                  <h4 class="font-semibold text-n-slate-12 mb-3 flex items-center gap-2">
                    Instruções Personalizadas de Leitura de Imagem
                  </h4>
                  <p class="text-n-slate-11 whitespace-pre-wrap break-words leading-relaxed">
                    {{ agentData.company.imagePrompt }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Verificar se tem tags ou times -->
            <template v-if="(!agentData.company.tagFields || agentData.company.tagFields.length === 0) && 
                           (!agentData.company.teamFields || agentData.company.teamFields.length === 0)">
              <!-- Modal único quando não tem nem tags nem times -->
              <div class="text-center py-12 bg-n-slate-1 rounded-xl border border-n-slate-3">
                <div class="i-lucide-settings text-n-slate-6 w-12 h-12 mx-auto mb-4"></div>
                <p class="text-n-slate-11 text-lg font-medium mb-2">Nenhuma configuração de tags ou times definida</p>
                <p class="text-n-slate-10 text-sm mb-4">Configure tags e times para organizar melhor as conversas do agente</p>
                <Button
                  variant="outline"
                  color="slate"
                  size="sm"
                  icon="i-lucide-edit-3"
                  @click="navigateToEdit"
                >
                  Editar Agente
                </Button>
              </div>
            </template>
            
            <template v-else>
              <!-- Tags -->
              <div v-if="agentData.company.tagFields && agentData.company.tagFields.length > 0">
                <h4 class="font-semibold text-n-slate-12 mb-4 flex items-center gap-2">
                  <div class="i-lucide-tags text-n-amber-9"></div>
                  Configuração de Tags
                </h4>
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div v-for="(tag, index) in agentData.company.tagFields" :key="`tag-${index}`" 
                       class="bg-n-amber-1 border border-n-amber-3 p-5 rounded-xl">
                    <div class="flex items-start gap-3">
                      <div class="bg-n-amber-9 p-2.5 rounded-full flex-shrink-0 h-10 w-10">
                        <span class="i-lucide-tag text-white w-5 h-5"></span>
                      </div>
                      <div class="flex-1">
                        <h5 class="font-semibold text-n-slate-12 mb-1">{{ tag.name }}</h5>
                        <p class="text-n-slate-11 leading-relaxed break-words">{{ tag.description || 'Sem descrição' }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Times -->
              <div v-if="agentData.company.teamFields && agentData.company.teamFields.length > 0">
                <h4 class="font-semibold text-n-slate-12 mb-4 flex items-center gap-2">
                  <div class="i-lucide-users text-n-teal-9"></div>
                  Atribuição de Times
                </h4>
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div v-for="(team, index) in agentData.company.teamFields" :key="`team-${index}`" 
                       class="bg-n-teal-1 border border-n-teal-3 p-5 rounded-xl">
                    <div class="flex items-start gap-3">
                      <div class="bg-n-teal-9 p-2.5 rounded-full flex-shrink-0 h-10 w-10">
                        <span class="i-lucide-users text-white w-5 h-5"></span>
                      </div>
                      <div class="flex-1">
                        <h5 class="font-semibold text-n-slate-12 mb-1">{{ team.name }}</h5>
                        <p class="text-n-slate-11 leading-relaxed break-words">{{ team.description || 'Sem descrição' }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <div v-if="activeTab === 'content'" class="tab-content w-full max-w-7xl space-y-6">
            <!-- Aba Base de Conhecimento -->
            <!-- Informações Complementares -->
            <div v-if="agentData.company.additionalInfo && agentData.company.additionalInfo.length > 0">
              <h4 class="font-semibold text-n-slate-12 mb-4 flex items-center gap-2">
                <div class="i-lucide-info text-n-brand"></div>
                Informações Complementares
              </h4>
              <div class="grid grid-cols-1 gap-4">
                <div v-for="(info, index) in agentData.company.additionalInfo" :key="`info-${index}`" 
                     class="bg-n-slate-1 p-4 rounded-xl border border-n-slate-3 hover:border-n-brand transition-all duration-200 cursor-pointer"
                     @click="toggleItemExpansion('additionalInfo', index)">
                  <div class="flex items-start justify-between gap-3">
                    <div class="flex-1">
                      <h5 class="font-semibold text-n-slate-12 mb-2">{{ info.name }}</h5>
                      <p v-if="!isItemExpanded('additionalInfo', index)" 
                         class="text-n-slate-11 leading-relaxed">
                        {{ getContentPreview(info.value) }}
                      </p>
                      <p v-else 
                         class="text-n-slate-11 whitespace-pre-wrap break-words leading-relaxed animate-fadeIn">
                        {{ info.value }}
                      </p>
                    </div>
                    <button type="button" class="flex-shrink-0 mt-1 text-n-slate-10 hover:text-n-slate-12 transition-colors">
                      <i :class="isItemExpanded('additionalInfo', index) ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" 
                         class="w-5 h-5"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>


            <!-- Mensagem quando não há Base de Conhecimento -->
            <div v-if="!agentData.company.additionalInfo || agentData.company.additionalInfo.length === 0" 
                 class="text-center py-12 bg-n-slate-1 rounded-xl border border-n-slate-3">
              <div class="i-lucide-file-text text-n-slate-6 w-12 h-12 mx-auto mb-4"></div>
              <p class="text-n-slate-11 text-lg font-medium mb-2">Nenhuma Informação Complementar definida</p>
              <p class="text-n-slate-10 text-sm mb-4">Adicione intruções e contexto para enriquecer o conhecimento do agente</p>
              <Button
                variant="outline"
                color="slate"
                size="sm"
                icon="i-lucide-edit-3"
                @click="navigateToEdit"
              >
                Editar Agente
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Debug Console Modal -->
    <div v-if="showDebugConsole"
         class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
         @click.self="toggleDebugConsole">
      <div class="bg-n-slate-1 dark:bg-n-slate-12 rounded-xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b border-n-slate-3 dark:border-n-slate-10">
          <div class="flex items-center gap-2">
            <i class="i-lucide-terminal text-n-amber-9"></i>
            <h3 class="text-lg font-semibold text-n-slate-12 dark:text-n-slate-1">
              Debug Console - JSON do Agente
            </h3>
          </div>
          <div class="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              color="blue"
              icon="i-lucide-copy"
              @click="copyJsonToClipboard"
            >
              Copiar JSON
            </Button>
            <Button
              size="sm"
              variant="outline"
              color="teal"
              :icon="showPasteJsonSection ? 'i-lucide-x' : 'i-lucide-clipboard-paste'"
              @click="togglePasteJsonSection"
            >
              {{ showPasteJsonSection ? 'Cancelar' : 'Colar JSON' }}
            </Button>
            <button
              @click="toggleDebugConsole"
              class="text-n-slate-10 hover:text-n-slate-12 dark:text-n-slate-4 dark:hover:text-n-slate-1 transition-colors"
            >
              <i class="i-lucide-x w-5 h-5"></i>
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-auto p-4 space-y-4">
          <!-- Seção de Colar JSON -->
          <div v-if="showPasteJsonSection" class="bg-n-teal-2 dark:bg-n-teal-11 rounded-lg p-4 border border-n-teal-4 dark:border-n-teal-9">
            <div class="flex items-center gap-2 mb-3">
              <i class="i-lucide-clipboard-paste text-n-teal-9"></i>
              <h4 class="text-sm font-semibold text-n-teal-11 dark:text-n-teal-3">
                Colar e Atualizar Agente
              </h4>
            </div>
            <textarea
              v-model="pastedJson"
              class="w-full h-64 px-3 py-2 bg-n-background dark:bg-n-slate-12 border border-n-slate-4 dark:border-n-slate-9 rounded-lg font-mono text-xs text-n-slate-11 dark:text-n-slate-3 focus:outline-none focus:ring-2 focus:ring-n-teal-9 resize-none"
              placeholder='Cole aqui o JSON do agente copiado... Exemplo:
{
  "id": 123,
  "name": "Nome do Agente",
  "voice": "female",
  ...
}'
            ></textarea>
            <div v-if="pasteJsonError" class="mt-2 p-2 bg-n-ruby-2 dark:bg-n-ruby-11 border border-n-ruby-4 dark:border-n-ruby-9 rounded text-xs text-n-ruby-11 dark:text-n-ruby-3 flex items-start gap-2">
              <i class="i-lucide-alert-circle w-4 h-4 flex-shrink-0 mt-0.5"></i>
              <span>{{ pasteJsonError }}</span>
            </div>
            <div class="flex items-center justify-between mt-3">
              <p class="text-xs text-n-teal-10 dark:text-n-teal-4 flex items-center gap-1">
                <i class="i-lucide-info w-3 h-3"></i>
                Cole o JSON copiado de outro agente para atualizar este agente
              </p>
              <Button
                size="sm"
                variant="solid"
                color="teal"
                icon="i-lucide-save"
                @click="validateAndUpdateAgent"
                :disabled="!pastedJson.trim()"
              >
                Atualizar Agente
              </Button>
            </div>
          </div>

          <!-- JSON Atual do Agente -->
          <div class="bg-n-slate-2 dark:bg-n-slate-11 rounded-lg p-4">
            <div class="flex items-center gap-2 mb-3">
              <i class="i-lucide-file-json text-n-slate-9"></i>
              <h4 class="text-sm font-semibold text-n-slate-11 dark:text-n-slate-3">
                JSON Atual do Agente
              </h4>
            </div>
            <pre class="font-mono text-xs text-n-slate-11 dark:text-n-slate-3 whitespace-pre-wrap break-all">{{ debugAgentJson }}</pre>
          </div>
        </div>

        <!-- Footer -->
        <div class="p-4 border-t border-n-slate-3 dark:border-n-slate-10 bg-n-amber-1 dark:bg-n-amber-12">
          <p class="text-xs text-n-amber-11 dark:text-n-amber-3 flex items-center gap-1">
            <i class="i-lucide-info w-3 h-3"></i>
            Modo Debug - Visualização somente leitura do JSON do agente
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Animação das abas */
.tab-content {
  animation: fadeInUp 0.3s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Classes para ajudar com texto longo */
.break-words {
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
}

/* Garantir que os containers tenham altura máxima e exibam scrollbar quando necessário */
.overflow-auto {
  overflow: auto;
}

/* Scroll suave */
.overflow-y-auto {
  scroll-behavior: smooth;
}

/* Barra de rolagem personalizada para o container principal */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  transition: background-color 0.3s ease;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.3);
}

.custom-scrollbar::-webkit-scrollbar-thumb:active {
  background-color: rgba(0, 0, 0, 0.4);
}

/* Tema escuro */
.dark .custom-scrollbar {
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.2);
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:active {
  background-color: rgba(255, 255, 255, 0.4);
}

.padding-no-scroll {
  padding-right: 10px; /* Padding padrão */
}

/* Estilos para o dropdown de inboxes */
.inbox-dropdown {
  position: relative;
}

.inbox-dropdown input[type="checkbox"] {
  accent-color: var(--color-n-brand);
}

/* Garantir que o dropdown não seja cortado */
.inbox-dropdown > div:last-child {
  position: fixed !important;
  z-index: 1;
}

.border-n-brand-dark-custom {
  border-color: rgb(39 129 246 / 1) !important;
}

.border-n-slate-4-dark-custom{
  border-color: rgb(var(--slate-4) / var(--tw-border-opacity, 1)) !important
}

.bg-n-alpha-black2 {
  background-color: rgba(0, 0, 0, 0.02);
}

.dark .bg-n-alpha-black2 {
  background-color: rgba(255, 255, 255, 0.02);
}

/* Animação para conteúdo expandido */
.animate-fadeIn {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>  